import React, { useMemo } from "react";
import { useUnidades } from "context/UnidadeContext";
import { useRelatorios } from "context/RelatorioContext";
import { useAprendizado } from "context/AprendizadoContext";
import { useDevices } from "context/DeviceContext";
import { BsExclamationDiamond } from "react-icons/bs";

interface Barra {
  unidadeName: string;
  progresso: number;
  somaDoDia: number;
  metaFinal: number;
}

interface Props {
  selectedDate: string;
  selectedUnidadeId: string | "Todas";
  selectedPeriodo: string;
}

export default function RelatorioMetasUnidades({ selectedDate, selectedUnidadeId, selectedPeriodo }: Props) {
  const { unidades } = useUnidades();
  const { relatorios } = useRelatorios();
  const { aprendizadoData } = useAprendizado();
  const { devices } = useDevices();

  const barras: Barra[] = useMemo(() => {
    const dataFiltro = new Date(selectedDate + "T12:00:00");
    if (isNaN(dataFiltro.getTime())) return [];

    const diaSemana = ["dom.", "seg.", "ter.", "qua.", "qui.", "sex.", "sab."][dataFiltro.getDay()];

    return unidades
      .filter(u => selectedUnidadeId === "Todos" || u.dispositivoId === selectedUnidadeId)
      .map((u) => {
        const apr = aprendizadoData.find((a) => a.dispositivoId === u.dispositivoId);
        const device = devices.find((d) => d.id === u.dispositivoId);
        if (!apr || !device) return null;

        const dataInicioAprendizado = new Date(apr.dataCadastro);
        const dataFimAprendizado = new Date(dataInicioAprendizado);
        dataFimAprendizado.setDate(dataInicioAprendizado.getDate() + 28);

        const expedientesConsiderados = selectedPeriodo === "Todos"
          ? Object.keys(device.expedientes || {})
          : [selectedPeriodo];

        let metaDiariaTotal = 0;

        expedientesConsiderados.forEach(nomeExpediente => {
          const relatoriosAprendizado = relatorios.filter((r) =>
            r.dispositivoId === u.dispositivoId &&
            r.DiaSemana === diaSemana &&
            r.expediente === nomeExpediente &&
            new Date(r.Data) >= dataInicioAprendizado &&
            new Date(r.Data) <= dataFimAprendizado
          );

          if (relatoriosAprendizado.length > 0) {
            const soma = relatoriosAprendizado.reduce((sum, r) => sum + r.contador, 0);
            const metaExpediente = soma / relatoriosAprendizado.length;
            metaDiariaTotal += metaExpediente;
          }
        });

        const relatoriosDoDia = relatorios.filter((r) =>
          r.dispositivoId === u.dispositivoId && 
          r.Data === selectedDate &&
          (selectedPeriodo === "Todos" || r.expediente === selectedPeriodo)
        );
        const somaDoDia = relatoriosDoDia.reduce((sum, r) => sum + r.contador, 0);
        const metaFinal = Math.round(metaDiariaTotal);
        const progressoNum = metaFinal > 0 ? Math.floor((somaDoDia / metaFinal) * 100) : 0;

        return {
          unidadeName: u.nome,
          progresso: progressoNum,
          somaDoDia: somaDoDia,
          metaFinal: metaFinal,
        };
      })
      .filter((b): b is Barra => b !== null);
  }, [selectedDate, selectedUnidadeId, selectedPeriodo, unidades, relatorios, aprendizadoData, devices]);


  const getColor = (p: number) => {
    if (p === 0) return "bg-gray-300";
    if (p < 60) return "bg-red-500";
    if (p < 90) return "bg-yellow-500";
    if (p < 120) return "bg-green-500";
    return "bg-red-500";
  };

  return (
    <div className="p-4 items-center bg-white rounded-xl shadow">
      <h2 className="text-lg font-bold text-navy-700">
        Média das Unidades
      </h2>
      <div className="items-center gap-4 pt-4">
        {barras.map((b, idx) => {
          const altura = Math.min(100, b.progresso);
          return (
            <div key={idx} className="flex flex-col items-center h-full w-full">
              <span className="text-sm font-bold h-[20px]">{b.somaDoDia} / {b.metaFinal} Pessoas</span>
              <div className="h-[200px] w-6 bg-gray-200 rounded-full flex items-end">
                <div
                  className={`${getColor(b.progresso)} w-full rounded-full transition-all duration-300 flex justify-center items-end pb-2`}
                  style={{ height: `${altura}%` }}
                >
                  {altura > 15 && (
                    <span className="text-white text-xs font-bold [writing-mode:vertical-rl]">
                      {b.progresso}%
                    </span>
                  )}
                </div>
              </div>
              <span className="text-xs text-gray-600 rotate-45 origin-bottom-left whitespace-nowrap">
                {b.unidadeName}
              </span>
            </div>
          );
        })}
        {barras.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <div className="mt-2 flex items-center justify-center rounded-full bg-lightPrimary p-[26px] text-5xl font-bold text-[#CF4949]">
              <BsExclamationDiamond />
            </div>
            <h3 className="text-xl font-bold text-navy-700 my-3">Nada Aqui</h3>
            <p className="text-center text-sm text-gray-600 px-4">
              Selecione uma <span className="font-semibold">Unidade</span> no filtro para visualizar.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
